import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { makeStyles } from "@saleor/macaw-ui";
import { Box, Button, Input, Text } from "@saleor/macaw-ui-next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DashboardCard } from "../components/Card";
import CardSpacer from "../components/CardSpacer";
import { TablePagination } from "../components/TablePagination/TablePagination";

const API_BASE = `${import.meta.env.VITE_FAST_API_URL}/api/account_address`;

type Buyer = {
  id: number | string;
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};

type BuyerFormValues = {
  id: number | string;
  first_name: string;
  last_name: string;
  phone: string;
};

const useStyles = makeStyles(
  theme => ({
    container: {
      margin: theme.spacing(2),
      background: theme.palette.background.default,
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      margin: "24px",
    },
    editButton: {
      minWidth: "40px",
      color: theme.palette.primary.main,
      "&:hover": {
        background: theme.palette.action.hover,
      },
    },
    dialogTitle: {
      padding: theme.spacing(2, 3),
    },
    dialogContent: {
      padding: theme.spacing(3),
    },
    dialogActions: {
      padding: theme.spacing(2),
    },
    formField: {
      marginBottom: theme.spacing(2),
    },
    nameCell: {
      fontWeight: 500,
    },
    tableHeader: {
      "& .MuiTableCell-root": {
        color: theme.palette.text.secondary,
        fontWeight: 600,
        fontSize: "1.4rem", // same as Saleor
        lineHeight: 1.0,
      },
    },
    actionButtons: {
      display: "flex",
      gap: theme.spacing(1),
      justifyContent: "flex-end",
    },
    activeRow: {
      background: theme.palette.action.selected,
    },
    exportButton: {
      marginLeft: theme.spacing(2),
    },
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
      gap: theme.spacing(2),
      marginBottom: theme.spacing(3),
    },
    statCard: {
      background: theme.palette.background.paper,
      borderRadius: theme.spacing(1),
      padding: theme.spacing(2),
      boxShadow: theme.shadows[1],
    },
    toolbar: {
      padding: theme.spacing(3, 3, 0, 3),
    },

    toolbarRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
    },

    filterButton: {
      background: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1, 2),
      borderRadius: theme.spacing(1),
      fontWeight: 500,
      textTransform: "none",
    },

    searchWrapper: {
      display: "flex",
      alignItems: "center",
      borderRadius: theme.spacing(1),
      padding: theme.spacing(1, 2),
      flex: 1,
      maxWidth: 400,
      transition: "background-color 0.2s ease",
      backgroundColor: theme.palette.background.paper,
      border: ` none ${theme.palette.divider}`,
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    },

    searchIcon: {
      color: theme.palette.text.secondary,
      marginRight: theme.spacing(1),
    },

    searchInput: {
      border: "none",
      outline: "none",
      width: "100%",
      fontSize: 14,
      backgroundColor: "transparent",
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
  }),
  { name: "BuyersPage" },
);

const normalizeBuyers = (data: unknown): Buyer[] => {
  if (Array.isArray(data)) {
    return data as Buyer[];
  }

  if (data && typeof data === "object") {
    const typed = data as { data?: Buyer[]; buyers?: Buyer[]; addresses?: Buyer[] };

    if (Array.isArray(typed.data)) {
      return typed.data;
    }

    if (Array.isArray(typed.buyers)) {
      return typed.buyers;
    }

    if (Array.isArray(typed.addresses)) {
      return typed.addresses;
    }
  }

  return [];
};

const getBuyerFirstName = (buyer: Buyer): string => buyer.first_name ?? buyer.firstName ?? "";
const getBuyerLastName = (buyer: Buyer): string => buyer.last_name ?? buyer.lastName ?? "";
const getBuyerPhone = (buyer: Buyer): string => buyer.phone ?? "";

const getFullName = (buyer: Buyer): string => {
  const firstName = getBuyerFirstName(buyer);
  const lastName = getBuyerLastName(buyer);

  return `${firstName} ${lastName}`.trim();
};

export const BuyersPage = (): JSX.Element => {
  const styles = useStyles();
  const [buyers, setBuyers] = useState<Buyer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isEditOpen, setEditOpen] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [activeBuyerId, setActiveBuyerId] = useState<number | string | null>(null);
  const [formValues, setFormValues] = useState<BuyerFormValues>({
    id: 0,
    first_name: "",
    last_name: "",
    phone: "",
  });
  const [page, setPage] = useState(0);
  const [rowNumber, setRowNumber] = useState(10);

  const rowRefs = useRef(new Map<number | string, HTMLTableRowElement | null>());

  const handleRowRef = useCallback(
    (buyerId: number | string): ((node: HTMLTableRowElement | null) => void) =>
      (node: HTMLTableRowElement | null): void => {
        rowRefs.current.set(buyerId, node);
      },
    [],
  );

  const loadBuyers = useCallback(async (signal?: AbortSignal): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/`, { signal });

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      const data = await response.json();
      const normalized = normalizeBuyers(data);

      setBuyers(normalized);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        return;
      }

      setError((err as Error).message || "Unknown error");
      setBuyers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    void loadBuyers(controller.signal);

    const abort = (): void => controller.abort();

    return abort;
  }, [loadBuyers]);

  const filteredBuyers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return buyers;
    }

    return buyers.filter(buyer => {
      const firstName = getBuyerFirstName(buyer).toLowerCase();
      const lastName = getBuyerLastName(buyer).toLowerCase();
      const fullName = `${firstName} ${lastName}`.toLowerCase();
      const phone = getBuyerPhone(buyer).toLowerCase();

      return (
        firstName.includes(query) ||
        lastName.includes(query) ||
        fullName.includes(query) ||
        phone.includes(query)
      );
    });
  }, [buyers, searchQuery]);

  const scrollToBuyer = useCallback((buyerId: number | string): void => {
    const row = rowRefs.current.get(buyerId);

    if (row) {
      row.scrollIntoView({ block: "nearest" });
    }
  }, []);

  const openEdit = (buyer: Buyer): void => {
    setFormValues({
      id: buyer.id,
      first_name: getBuyerFirstName(buyer),
      last_name: getBuyerLastName(buyer),
      phone: getBuyerPhone(buyer),
    });
    setActiveBuyerId(buyer.id);
    scrollToBuyer(buyer.id);
    setEditOpen(true);
  };

  const closeEdit = (): void => setEditOpen(false);

  const handleFieldChange =
    (field: keyof BuyerFormValues): ((value: string) => void) =>
    (value: string): void => {
      setFormValues(prev => ({ ...prev, [field]: value }));
    };

  const handleSave = async (): Promise<void> => {
    setSaving(true);

    try {
      const payload = {
        first_name: formValues.first_name.trim(),
        last_name: formValues.last_name.trim(),
        phone: formValues.phone.trim(),
      };

      const response = await fetch(`${API_BASE}/${formValues.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Save failed (HTTP ${response.status})`);
      }

      setEditOpen(false);
      await loadBuyers();
    } catch (err) {
      alert("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const hasBuyers = filteredBuyers.length > 0;
  const showEmptyState = !loading && !error && !hasBuyers;

  const totalPages = Math.max(1, Math.ceil(filteredBuyers.length / rowNumber));
  const currentPage = Math.min(page, totalPages - 1);
  const pagedBuyers = useMemo((): Buyer[] => {
    const start = currentPage * rowNumber;
    const end = start + rowNumber;

    return filteredBuyers.slice(start, end);
  }, [filteredBuyers, currentPage, rowNumber]);

  const activeBuyerName = useMemo((): string => {
    const buyer = buyers.find(item => item.id === activeBuyerId);

    if (!buyer) {
      return "";
    }

    return getFullName(buyer);
  }, [activeBuyerId, buyers]);

  const handlePageChange = (direction: "next" | "prev"): void => {
    setPage(prev => {
      if (direction === "next") {
        return Math.min(prev + 1, totalPages - 1);
      }

      return Math.max(prev - 1, 0);
    });
  };

  const handleRowNumberChange = (value: number): void => {
    setRowNumber(value);
    setPage(0);
  };

  const escapeCsvValue = (value: string): string => {
    const needsQuotes = /[",\n]/.test(value);
    const escaped = value.replace(/"/g, '""');

    return needsQuotes ? `"${escaped}"` : escaped;
  };

  const handleExport = (): void => {
    const headers = ["first_name", "last_name", "phone"];
    const rows = filteredBuyers.map(buyer => [
      getBuyerFirstName(buyer),
      getBuyerLastName(buyer),
      getBuyerPhone(buyer),
    ]);
    const csv = [headers, ...rows]
      .map(row => row.map(value => escapeCsvValue(value ?? "")).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "customer_export.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box className={styles.container}>
      {/* Header */}
      <Box className={styles.header}>
        <Box>
          <Text as="h1" size={8} fontWeight="bold">
            Customer List
          </Text>
          <Text size={3} color="default2">
            Manage buyer directory and contact information
          </Text>
        </Box>

        <Button
          variant="primary"
          onClick={handleExport}
          disabled={!hasBuyers}
          className={styles.exportButton}
        >
          Export CSV
        </Button>
      </Box>

      {/* Table Card */}
      <DashboardCard>
        <Box className={styles.toolbar}>
          <Box className={styles.toolbarRow}>
            <Box className={styles.searchWrapper}>
              <SearchIcon className={styles.searchIcon} />
              <input
                className={styles.searchInput}
                value={searchQuery}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setSearchQuery(event.target.value);
                  setPage(0);
                }}
                placeholder="Search customers..."
              />
            </Box>
          </Box>
        </Box>

        {loading && (
          <Box padding={4}>
            <Text color="default2">Loading...</Text>
          </Box>
        )}

        {error && (
          <Box padding={4}>
            <Text color="critical1">Error loading customers</Text>
          </Box>
        )}

        {showEmptyState && (
          <Box padding={4}>
            <Text color="default2">No custoner found</Text>
          </Box>
        )}

        {hasBuyers && (
          <>
            <Table>
              <TableHead className={styles.tableHeader}>
                <TableRow>
                  <TableCell>First Name</TableCell>
                  <TableCell>Last Name</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {pagedBuyers.map(buyer => (
                  <TableRow
                    key={buyer.id}
                    hover
                    ref={handleRowRef(buyer.id)}
                    className={buyer.id === activeBuyerId ? styles.activeRow : undefined}
                  >
                    <TableCell>
                      <Text className={styles.nameCell} fontWeight="medium">
                        {getBuyerFirstName(buyer) || "-"}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Text className={styles.nameCell} fontWeight="medium">
                        {getBuyerLastName(buyer) || "-"}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Text>{getBuyerPhone(buyer) || "-"}</Text>
                    </TableCell>
                    <TableCell align="right">
                      <Box className={styles.actionButtons}>
                        <Button
                          variant="tertiary"
                          size="small"
                          onClick={() => openEdit(buyer)}
                          className={styles.editButton}
                        >
                          Edit
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Box paddingTop={4}>
              <TablePagination
                rowNumber={rowNumber}
                onRowNumberChange={handleRowNumberChange}
                hasNextPage={currentPage < totalPages - 1}
                hasPreviousPage={currentPage > 0}
                onNextPage={() => handlePageChange("next")}
                onPreviousPage={() => handlePageChange("prev")}
              />
            </Box>
          </>
        )}
      </DashboardCard>

      {activeBuyerName && <CardSpacer />}

      {/* Dialog */}
      <Dialog open={isEditOpen} onClose={closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>
          <Text size={4} fontWeight="bold">
            Edit Buyer
          </Text>
        </DialogTitle>

        <DialogContent className={styles.dialogContent}>
          <Box className={styles.formField}>
            <Input
              label="First Name"
              value={formValues.first_name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleFieldChange("first_name")(e.target.value)
              }
              width="100%"
              size="small"
            />
          </Box>
          <Box className={styles.formField}>
            <Input
              label="Last Name"
              value={formValues.last_name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleFieldChange("last_name")(e.target.value)
              }
              width="100%"
              size="small"
            />
          </Box>
          <Box className={styles.formField}>
            <Input
              label="Phone"
              value={formValues.phone}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleFieldChange("phone")(e.target.value)
              }
              width="100%"
              size="small"
            />
          </Box>
        </DialogContent>

        <DialogActions className={styles.dialogActions}>
          <Button onClick={closeEdit} variant="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="primary" disabled={isSaving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {activeBuyerName && (
        <Box marginTop={2}>
          <Text color="default2" size={2}>
            Currently editing: {activeBuyerName}
          </Text>
        </Box>
      )}
    </Box>
  );
};

BuyersPage.displayName = "BuyersPage";
